import { ThemeEvents } from '@theme/events';

const SHOPIFY_MONEY_DIVISOR = 100;

class OasisVolumeDiscount extends HTMLElement {
  connectedCallback() {
    this.variantData = this.#readVariantData();
    this.productForm = this.closest('product-form-component');
    this.quantitySelector = this.productForm?.querySelector('quantity-selector-component');
    this.quantityInput = this.productForm?.querySelector('input[name="quantity"]');
    this.currentQuantity = Number(this.dataset.selectedTier) || this.#getTierQuantities()[0] || 1;
    this.optionsExpanded = this.dataset.optionsCollapsed !== 'true';

    this.#bindEvents();
    this.#syncOptionSelectsToVariant(this.dataset.selectedVariantId);
    this.#applyQuantity(this.currentQuantity, { syncQuantity: true });
  }

  getCartItems() {
    const variants = this.#getSelectedVariants();
    const appliedTier = this.#getAppliedTier(this.currentQuantity);
    const discount = Number(appliedTier?.discount) || 0;
    const label = appliedTier?.label || `${this.currentQuantity} items`;
    const propertyPrefix = this.dataset.propertyPrefix || '_orthocloud_volume_discount';
    const properties = {
      _oasis_volume_discount: label,
      _oasis_volume_discount_quantity: String(this.currentQuantity),
      _oasis_volume_discount_percent: String(discount),
      [propertyPrefix]: label,
      [`${propertyPrefix}_quantity`]: String(this.currentQuantity),
      [`${propertyPrefix}_percent`]: String(discount),
    };

    return variants.reduce((items, variant) => {
      if (!variant?.id) return items;

      const key = String(variant.id);
      const existing = items.find((item) => item.variantId === key);

      if (existing) {
        existing.quantity += 1;
      } else {
        items.push({
          variantId: key,
          quantity: 1,
          properties,
        });
      }

      return items;
    }, []);
  }

  #bindEvents() {
    this.addEventListener('change', (event) => {
      const target = event.target;

      if (target instanceof HTMLInputElement && target.matches('[data-oasis-tier-input]')) {
        this.#applyQuantity(Number(target.value) || 1, { syncQuantity: true });
        return;
      }

      if (target instanceof HTMLSelectElement && target.matches('[data-oasis-option-select]')) {
        this.#render();
      }
    });

    this.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || !target.matches('[data-oasis-options-toggle]')) return;

      this.optionsExpanded = !this.optionsExpanded;
      this.#syncOptionsVisibility();
    });

    this.productForm?.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
      const quantity = Number(event.detail?.quantity);
      if (!quantity || quantity === this.currentQuantity) return;
      this.#applyQuantity(quantity, { syncQuantity: false });
    });

    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantUpdate, (event) => {
      const variant = event.detail?.resource;
      if (!variant?.id) return;

      this.dataset.selectedVariantId = String(variant.id);
      this.#syncOptionSelectsToVariant(String(variant.id));
      this.#render();
    });
  }

  #applyQuantity(quantity, { syncQuantity }) {
    this.currentQuantity = Math.max(1, Math.floor(quantity));
    this.dataset.selectedTier = String(this.currentQuantity);

    this.querySelectorAll('[data-oasis-tier-card]').forEach((card) => {
      const isSelected = Number(card.dataset.quantity) === this.currentQuantity;
      card.classList.toggle('is-selected', isSelected);
      const input = card.querySelector('input');
      input?.toggleAttribute('checked', isSelected);
      if (input) input.checked = isSelected;
    });

    if (syncQuantity) {
      this.#syncQuantitySelector();
    }

    this.#render();
  }

  #syncQuantitySelector() {
    const nextValue = String(this.currentQuantity);

    if (this.quantitySelector?.setValue) {
      this.quantitySelector.setValue(nextValue);
      this.quantitySelector.updateButtonStates?.();
      this.quantitySelector.dispatchEvent(
        new CustomEvent(ThemeEvents.quantitySelectorUpdate, {
          bubbles: true,
          detail: { quantity: this.currentQuantity },
        })
      );
    } else if (this.quantityInput instanceof HTMLInputElement) {
      this.quantityInput.value = nextValue;
    }
  }

  #syncOptionSelectsToVariant(variantId) {
    if (!variantId) return;

    this.querySelectorAll('[data-oasis-option-select]').forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;
      if ([...select.options].some((option) => option.value === String(variantId))) {
        select.value = String(variantId);
      }
    });
  }

  #render() {
    this.#updatePrices();
    this.#updateCustomSummary();
    this.#syncOptionsVisibility();
  }

  #updatePrices() {
    const baseVariant = this.#getVariantById(this.dataset.selectedVariantId) || this.#getSelectedVariants()[0];
    const basePrice = Number(baseVariant?.price) || 0;

    this.querySelectorAll('[data-oasis-tier-card]').forEach((card) => {
      const quantity = Number(card.dataset.quantity) || 1;
      const discount = Number(card.dataset.discount) || 0;
      let compareTotal = basePrice * quantity;
      let discountedTotal = Math.round((compareTotal * (100 - discount)) / 100);

      if (quantity === this.currentQuantity) {
        const selectedSubtotal = this.#getSelectedVariants().reduce(
          (sum, variant) => sum + (Number(variant?.price) || basePrice),
          0
        );
        compareTotal = selectedSubtotal;
        discountedTotal = Math.round((selectedSubtotal * (100 - discount)) / 100);
      }

      const priceNode = card.querySelector('[data-oasis-tier-price]');
      const compareNode = card.querySelector('[data-oasis-tier-compare]');
      const savingsNode = card.querySelector('[data-oasis-tier-savings]');

      if (priceNode) priceNode.textContent = this.#format(discountedTotal);

      if (compareNode) {
        compareNode.textContent = this.#format(compareTotal);
        compareNode.hidden = discount === 0;
      }

      if (savingsNode) {
        savingsNode.textContent = `Save ${this.#format(compareTotal - discountedTotal)}`;
        savingsNode.hidden = discount === 0;
      }
    });
  }

  #updateCustomSummary() {
    const summary = this.querySelector('[data-oasis-custom-summary]');
    if (!(summary instanceof HTMLElement)) return;

    const exactTier = this.#getExactTier(this.currentQuantity);
    summary.hidden = Boolean(exactTier);
    if (exactTier) return;

    const appliedTier = this.#getAppliedTier(this.currentQuantity);
    const discount = Number(appliedTier?.discount) || 0;
    const variants = this.#getSelectedVariants();
    const compareTotal = variants.reduce((sum, variant) => sum + (Number(variant?.price) || 0), 0);
    const discountedTotal = Math.round((compareTotal * (100 - discount)) / 100);
    const savings = compareTotal - discountedTotal;
    const perItem = Math.round(discountedTotal / this.currentQuantity);
    const label = `${this.currentQuantity} ${this.#unitLabel(this.currentQuantity)}`;

    const totalNode = summary.querySelector('[data-oasis-custom-total]');
    const compareNode = summary.querySelector('[data-oasis-custom-compare]');
    const savingsNode = summary.querySelector('[data-oasis-custom-savings]');
    const eachNode = summary.querySelector('[data-oasis-custom-each]');

    if (totalNode) totalNode.textContent = `${label} — ${this.#format(discountedTotal)}`;

    if (compareNode) {
      compareNode.textContent = `Was ${this.#format(compareTotal)}`;
      compareNode.hidden = discount === 0 || savings <= 0;
    }

    if (savingsNode) {
      savingsNode.textContent = `You save ${this.#format(savings)}`;
      savingsNode.hidden = discount === 0 || savings <= 0;
    }

    if (eachNode) {
      eachNode.textContent = `${this.#format(perItem)} each${discount > 0 ? ` (${discount}% off)` : ''}`;
    }
  }

  #syncOptionsVisibility() {
    const options = this.querySelector('[data-oasis-options]');
    const toggle = this.querySelector('[data-oasis-options-toggle]');
    const note = this.querySelector('[data-oasis-options-note]');
    const selectorLimit = this.#getSelectorLimit();

    if (toggle instanceof HTMLButtonElement) {
      toggle.setAttribute('aria-expanded', String(this.optionsExpanded));
    }

    if (options instanceof HTMLElement) {
      options.hidden = !this.optionsExpanded;
    }

    this.querySelectorAll('[data-oasis-option-row]').forEach((row) => {
      const itemIndex = Number(row.dataset.itemIndex);
      row.hidden = !this.optionsExpanded || itemIndex > Math.min(this.currentQuantity, selectorLimit);
    });

    if (note instanceof HTMLElement) {
      note.hidden = !this.optionsExpanded || this.currentQuantity <= selectorLimit;
    }
  }

  #getSelectedVariants() {
    const selects = [...this.querySelectorAll('[data-oasis-option-select]')].filter(
      (select) => select instanceof HTMLSelectElement
    );

    if (selects.length === 0) {
      const selectedVariant = this.#getVariantById(this.dataset.selectedVariantId) || this.variantData[0];
      return Array.from({ length: this.currentQuantity }, () => selectedVariant).filter(Boolean);
    }

    const fallbackVariant = this.#getVariantById(this.dataset.selectedVariantId) || this.variantData[0];
    const variants = [];

    for (let index = 0; index < this.currentQuantity; index += 1) {
      const select = selects[index];
      variants.push(select ? this.#getVariantById(select.value) : fallbackVariant);
    }

    return variants.filter(Boolean);
  }

  #getExactTier(quantity) {
    return this.#getTiers().find((tier) => tier.quantity === quantity) || null;
  }

  #getAppliedTier(quantity) {
    return this.#getTiers().reduce((applied, tier) => {
      if (quantity >= tier.quantity && (!applied || tier.quantity > applied.quantity)) {
        return tier;
      }

      return applied;
    }, null);
  }

  #getSelectorLimit() {
    return this.querySelectorAll('[data-oasis-option-row]').length || 0;
  }

  #getTiers() {
    return [...this.querySelectorAll('[data-oasis-tier-card]')]
      .map((card) => ({
        quantity: Number(card.dataset.quantity),
        discount: Number(card.dataset.discount) || 0,
        label: card.dataset.label || '',
      }))
      .filter((tier) => tier.quantity)
      .sort((a, b) => a.quantity - b.quantity);
  }

  #getTierQuantities() {
    return this.#getTiers().map((tier) => tier.quantity);
  }

  #getVariantById(variantId) {
    return this.variantData.find((variant) => String(variant.id) === String(variantId));
  }

  #readVariantData() {
    const script = this.querySelector('[data-oasis-variants]');
    if (!script?.textContent) return [];

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Failed to parse Oasis volume discount variant data', error);
      return [];
    }
  }

  #format(shopifyMoneyValue) {
    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    const value = Number(shopifyMoneyValue) || 0;

    if (typeof window.Shopify?.formatMoney === 'function') {
      return window.Shopify.formatMoney(value, moneyFormat);
    }

    return this.#formatShopifyMoney(value, moneyFormat);
  }

  #formatShopifyMoney(shopifyMoneyValue, moneyFormat) {
    const currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';

    return moneyFormat.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = 2;

      switch (placeholder) {
        case 'amount':
          break;
        case 'amount_no_decimals':
          precision = 0;
          break;
        case 'amount_with_comma_separator':
          thousandsSeparator = '.';
          decimalSeparator = ',';
          break;
        case 'amount_no_decimals_with_comma_separator':
          thousandsSeparator = '.';
          decimalSeparator = ',';
          precision = 0;
          break;
        case 'amount_no_decimals_with_space_separator':
          thousandsSeparator = ' ';
          precision = 0;
          break;
        case 'amount_with_space_separator':
          thousandsSeparator = ' ';
          decimalSeparator = ',';
          break;
        case 'amount_with_period_and_space_separator':
          thousandsSeparator = ' ';
          decimalSeparator = '.';
          break;
        case 'amount_with_apostrophe_separator':
          thousandsSeparator = "'";
          decimalSeparator = '.';
          break;
        default:
          break;
      }

      return this.#formatShopifyAmount(shopifyMoneyValue, {
        decimalSeparator,
        precision,
        thousandsSeparator,
      });
    });
  }

  #formatShopifyAmount(shopifyMoneyValue, { thousandsSeparator, decimalSeparator, precision }) {
    const roundedNumber = (shopifyMoneyValue / SHOPIFY_MONEY_DIVISOR).toFixed(precision);
    let [whole, fraction = ''] = roundedNumber.split('.');

    if (!whole) whole = '0';
    whole = whole.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? whole : whole + decimalSeparator + fraction.padEnd(precision, '0');
  }

  #unitLabel(quantity) {
    return quantity === 1
      ? this.dataset.unitSingular || 'item'
      : this.dataset.unitPlural || 'items';
  }
}

if (!customElements.get('oasis-volume-discount')) {
  customElements.define('oasis-volume-discount', OasisVolumeDiscount);
}
